import { computed, nextTick } from 'vue';
import { CampaignAttachEntitiesChange, CampaignDetachEntitiesChange } from '@studyportals/campaign-management-api-interface';
import { ModalErrorStates } from '@/models/modal/modal.enum';
import { DispatchedEvents } from '@/models/dispatched-events.enum';
import { IPresentableEntity } from '@/models/available-entities.interface';
import { LoadingStatuses } from '@/models/loading.interface';
import campaignPresenter from '@/presenters/campaign-presenter';
import store from '@/store';

export default class GeotargetingIncentiveCode {
	private domainName = 'campaign-editor-frontend';

	public isLoading = computed((): boolean => {
		const loadingStatus =  store.getters.loadingStatusEntities();
		return loadingStatus === LoadingStatuses.ACTIVE;
	});

	public isIncentiveDisabled = computed((): boolean => {
		return !this.isGeotargetingMicroserviceReady.value || !this.isAnythingAttached.value || this.hasAttachedEntityDataChanged.value;
	});

	public isGeotargetingMicroserviceReady = computed((): boolean => {
		return store.getters.isGeotargetingMicroserviceReady();
	});

	public isAnythingAttached = computed((): boolean => {
		return this.attachedStudies.value > 0 || this.attachedOrganisations.value > 0;
	});

	public attachedStudies = computed((): number => {
		return this.availableEntities.value.filter((entity) => entity.attached && !entity.isOrganisation).length;
	});

	public attachedOrganisations = computed((): number => {
		return this.availableEntities.value.filter((entity) => entity.attached && entity.isOrganisation).length;
	});

	public hasAttachedEntityDataChanged = computed((): boolean => {
		return this.attachedEntityChanges.value !== undefined || this.detachedEntityChanges.value !== undefined;
	});

	public newlyAttachedStudies = computed((): number => {
		return this.attachedEntityChanges.value ? this.attachedEntityChanges.value.value.studyIds.length : 0;
	});

	public newlyAttachedOrganisations = computed((): number => {
		return this.attachedEntityChanges.value ? this.attachedEntityChanges.value.value.organisationIds.length : 0;
	});

	public newlyDetachedStudies = computed((): number => {
		return this.detachedEntityChanges.value ? this.detachedEntityChanges.value.value.studyIds.length : 0;
	});

	public newlyDetachedOrganisations = computed((): number => {
		return this.detachedEntityChanges.value ? this.detachedEntityChanges.value.value.organisationIds.length : 0;
	});

	public availableEntities = computed((): IPresentableEntity[] => {
		return store.getters.availableEntities();
	});

	private attachedEntityChanges = computed((): CampaignAttachEntitiesChange | undefined => {
		return campaignPresenter.retrieveAttachedEntityChanges();
	});

	private detachedEntityChanges = computed((): CampaignDetachEntitiesChange | undefined => {
		return campaignPresenter.retrieveDetachedEntityChanges();
	});

	private campaignId = computed((): number => {
		const router = store.getters.router();
		return router.props.id;
	});

	private startDate = computed((): number => {
		const startDate = store.getters.startDate();
		if (!startDate) {
			return 0;
		}

		return Math.round(startDate.getTime() / 1000);
	});

	public openGeotargetingTool(): void {
		document.dispatchEvent(new Event(DispatchedEvents.GEOTARGETING_START_UP));
		// Only show an error when the campaign editor is running standalone.
		if (!window.location.hostname.includes(this.domainName)) {
			return;
		}

		this.showGeotargetingNotAvailableMessage();
	}

	public async prepareForLoadingGeotargeting(): Promise<void> {
		if (this.isLoading.value) {
			return;
		}

		await nextTick();

		const campaignEditorPlaceholder: HTMLElement | null = document.querySelector('.js-campaignEditorPlaceholder');
		if (!campaignEditorPlaceholder || !this.campaignId.value || !this.startDate.value) {
			return;
		}

		campaignEditorPlaceholder.dataset.campaignId = this.campaignId.value.toString();
		campaignEditorPlaceholder.dataset.campaignStartDate = this.startDate.value.toString();

		document.dispatchEvent(new Event(DispatchedEvents.CAMPAIGN_EDITOR_READY_TO_LOAD_GEOTARGETING));
	}

	private showGeotargetingNotAvailableMessage(): void {
		store.mutations.setModalErrorState(ModalErrorStates.LOADING_GEOTARGETING_WHEN_STAND_ALONE);
	}
}
